import {
  FieldFilterMatch,
  FieldType,
  useAllFieldsQuery,
  useAllTagsQuery,
} from '@transactions/graphql';
import * as React from 'react';
import {FilterPill} from 'src/components/FilterDropdown';
import {TextInput} from 'src/inputs/TextInput';
import Popover from 'src/Layer/Popover';
import {Body, Container, Footer, Header} from 'src/PopoverForm';
import {compareTagsNewestFirst} from 'src/tagOrdering';
import {useSearch} from 'src/useSearch';
import type {FieldFilterInput as FieldFilterInputType} from '@transactions/graphql';

type Props = {
  tags: string[] | null;
  setTags: (tags: string[] | null) => void;
  fieldFilters: FieldFilterInputType[] | null;
  setFieldFilters: (filters: FieldFilterInputType[] | null) => void;
};

export const DimensionsFilterInput = (props: Props) => {
  const {tags, setTags, fieldFilters, setFieldFilters} = props;
  const {data: tagsData, loading: tagsLoading} = useAllTagsQuery();
  const {data: fieldsData, loading: fieldsLoading} = useAllFieldsQuery();
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const allTags = tagsData?.allTags ?? [];
  const allFields = fieldsData?.allFields ?? [];

  const {search: searchTags} = useSearch(allTags, ['name'], {
    tieBreak: compareTagsNewestFirst,
  });
  const {search: searchFields} = useSearch(allFields, ['name']);

  const filteredTags = searchTags(query);
  const filteredFields = searchFields(query);

  // Normalize: drop tag IDs that no longer exist in allTags (e.g. deleted tags
  // persisted in localStorage). This mirrors the old TagFilterInput behavior
  // where the selection was always derived via all.filter(t => tags.includes(t._id)).
  const knownTagIds = React.useMemo(
    () => new Set(allTags.map((t) => t._id)),
    [allTags],
  );
  const activeTags = React.useMemo(
    () => (tags ? tags.filter((id) => knownTagIds.has(id)) : null),
    [tags, knownTagIds],
  );

  const activeCount = (activeTags?.length ?? 0) + (fieldFilters?.length ?? 0);
  const hasActive = activeCount > 0;

  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
  };

  // Tag handlers
  const handleTagToggle = (tagId: string) => {
    const current = activeTags ?? [];
    const next = current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId];
    setTags(next.length > 0 ? next : null);
  };

  // Field handlers (lifted from FieldFilterInput)
  const filterByFieldId = (fieldId: string): FieldFilterInputType | undefined =>
    fieldFilters?.find((f) => f.fieldId === fieldId);

  const setFilter = (fieldId: string, filter: FieldFilterInputType | null) => {
    const others = (fieldFilters || []).filter((f) => f.fieldId !== fieldId);
    if (!filter) {
      setFieldFilters(others.length > 0 ? others : null);
    } else {
      setFieldFilters([...others, filter]);
    }
  };

  const handleEnumChange = (fieldId: string, value: string) => {
    if (value === '') {
      setFilter(fieldId, null);
    } else if (value === '__UNSET__') {
      setFilter(fieldId, {fieldId, match: FieldFilterMatch.Unset});
    } else {
      setFilter(fieldId, {fieldId, match: FieldFilterMatch.Exact, value});
    }
  };

  const handleTextChange = (fieldId: string, value: string) => {
    if (value === '') {
      setFilter(fieldId, null);
    } else {
      setFilter(fieldId, {fieldId, match: FieldFilterMatch.Contains, value});
    }
  };

  const handleTextUnset = (fieldId: string, checked: boolean) => {
    if (checked) {
      setFilter(fieldId, {fieldId, match: FieldFilterMatch.Unset});
    } else {
      setFilter(fieldId, null);
    }
  };

  const enumSelectedValue = (fieldId: string): string => {
    const filter = filterByFieldId(fieldId);
    if (!filter) return '';
    if (filter.match === FieldFilterMatch.Unset) return '__UNSET__';
    return filter.value ?? '';
  };

  const textInputValue = (fieldId: string): string => {
    const filter = filterByFieldId(fieldId);
    if (!filter || filter.match === FieldFilterMatch.Unset) return '';
    return filter.value ?? '';
  };

  const isTextUnset = (fieldId: string): boolean => {
    const filter = filterByFieldId(fieldId);
    return filter?.match === FieldFilterMatch.Unset;
  };

  const handleClearAll = () => {
    setTags(null);
    setFieldFilters(null);
    setQuery('');
    setIsOpen(false);
  };

  if ((tagsLoading && !tagsData) && (fieldsLoading && !fieldsData)) {
    return null;
  }

  if (allTags.length === 0 && allFields.length === 0) {
    return null;
  }

  const popoverContents = (
    <Container className="min-w-[280px]">
      <Header>
        <TextInput
          autoFocus
          type="text"
          placeholder="Search tags and fields..."
          value={query}
          onChange={setQuery}
        />
      </Header>
      <Body>
        {allTags.length > 0 && (
          <div className="mb-3">
            <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
              Tags
            </p>
            {filteredTags.length === 0 ? (
              <p className="text-muted-foreground py-1 text-xs">No tags match.</p>
            ) : (
              filteredTags.map((tag) => (
                <label
                  key={tag._id}
                  className="flex cursor-pointer items-center gap-2 py-0.5 text-sm"
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5"
                    checked={activeTags?.includes(tag._id) ?? false}
                    onChange={() => handleTagToggle(tag._id)}
                  />
                  {tag.name}
                </label>
              ))
            )}
          </div>
        )}
        {allTags.length > 0 && allFields.length > 0 && (
          <hr className="border-border my-2" />
        )}
        {allFields.length > 0 && (
          <div>
            <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
              Fields
            </p>
            {filteredFields.length === 0 ? (
              <p className="text-muted-foreground py-1 text-xs">No fields match.</p>
            ) : (
              filteredFields.map((field) => (
                <div key={field._id} className="mb-2 flex flex-col gap-1">
                  <label className="text-muted-foreground text-xs">{field.name}</label>
                  {field.type === FieldType.Text ? (
                    <div className="flex flex-col gap-1.5">
                      <input
                        type="text"
                        className="border-input bg-background rounded-md border px-2 py-1 text-sm"
                        placeholder="Contains..."
                        value={textInputValue(field._id)}
                        disabled={isTextUnset(field._id)}
                        onChange={(e) => handleTextChange(field._id, e.target.value)}
                      />
                      <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          className="h-3 w-3"
                          checked={isTextUnset(field._id)}
                          onChange={(e) => handleTextUnset(field._id, e.target.checked)}
                        />
                        Unset only
                      </label>
                    </div>
                  ) : (
                    <select
                      className="border-input bg-background rounded-md border px-2 py-1 text-sm"
                      value={enumSelectedValue(field._id)}
                      onChange={(e) => handleEnumChange(field._id, e.target.value)}
                    >
                      <option value="">Any</option>
                      <option value="__UNSET__">Unset</option>
                      {field.values.map((v) => (
                        <option key={v.value} value={v.value}>
                          {v.value}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </Body>
      {hasActive && (
        <Footer>
          <button
            className="text-muted-foreground hover:text-foreground w-full text-left text-xs"
            onClick={handleClearAll}
          >
            Clear all
          </button>
        </Footer>
      )}
    </Container>
  );

  return (
    <Popover
      open={isOpen}
      popoverContents={popoverContents}
      onOutsideClick={handleClose}
    >
      {(ref: React.RefCallback<HTMLButtonElement>) => (
        <FilterPill
          anchorRef={ref}
          onClick={() => (isOpen ? handleClose() : setIsOpen(true))}
          label="Dimensions"
          value={hasActive ? String(activeCount) : null}
          active={hasActive}
        />
      )}
    </Popover>
  );
};
